import { AnyAction } from 'redux';
import { combineEpics } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, withLatestFrom } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { ApiError } from '@bolteu/react-api-gateway/es';

import { selectSelectedProvider } from 'rdx/account';
import { GlobalAppActions } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { GlobalAppState } from 'rdx/state';

import { ProviderOfflinePeriodStatus } from '../../../../common/all/api/gen/DeliveryProviderPortalAuthApi';
import {
  cancelOfflinePeriodAsyncAction,
  createOfflinePeriodAsyncAction,
  getOfflinePeriodsAsyncAction,
} from './actions';

const getOfflinePeriodsEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient, bugsnag }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(getOfflinePeriodsAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([_action, state]) => {
      const selectedProvider = selectSelectedProvider(state);

      if (!selectedProvider || !selectedProvider.providerId) {
        return of(getOfflinePeriodsAsyncAction.failure(new Error('Missed selected provider id')));
      }

      return from(
        apiClient.portal.getProviderOfflinePeriods({
          provider_id: selectedProvider.providerId,
          statuses: [ProviderOfflinePeriodStatus.PENDING, ProviderOfflinePeriodStatus.STARTED],
        }),
      ).pipe(
        switchMap((res) => {
          const sideEffectActions: AnyAction[] = [];

          return of(getOfflinePeriodsAsyncAction.success(res), ...sideEffectActions);
        }),
        catchError((e) => {
          if (!ApiError.isApiError(e)) {
            bugsnag.notify(e);
          }
          return of(getOfflinePeriodsAsyncAction.failure(e));
        }),
      );
    }),
  );
};

const createOfflinePeriodEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient, bugsnag }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(createOfflinePeriodAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([action, _state]) => {
      if (!action.payload.provider_id) {
        return of(
          createOfflinePeriodAsyncAction.failure({
            response: { error_hint: 'Missed items or selected provider' },
          }),
        );
      }

      return from(apiClient.portal.createProviderOfflinePeriod(action.payload)).pipe(
        switchMap((response) => {
          return of(
            createOfflinePeriodAsyncAction.success(response),
            getOfflinePeriodsAsyncAction.request(),
          );
        }),
        catchError((e) => {
          if (!ApiError.isApiError(e)) {
            bugsnag.notify(e);
          }
          return of(createOfflinePeriodAsyncAction.failure(e));
        }),
      );
    }),
  );
};

const cancelOfflinePeriodEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient, bugsnag }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(cancelOfflinePeriodAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([action, _state]) => {
      if (!action.payload.provider_id) {
        return of(
          cancelOfflinePeriodAsyncAction.failure(new Error('Missed items or selected provider')),
        );
      }

      return from(apiClient.portal.cancelProviderOfflinePeriod(action.payload)).pipe(
        switchMap((res) => {
          return of(
            cancelOfflinePeriodAsyncAction.success(res),
            getOfflinePeriodsAsyncAction.request(),
          );
        }),
        catchError((e) => {
          if (!ApiError.isApiError(e)) {
            bugsnag.notify(e);
          }
          return of(cancelOfflinePeriodAsyncAction.failure(e));
        }),
      );
    }),
  );
};
export const offlinePeriodsEpic = combineEpics(
  getOfflinePeriodsEpic,
  createOfflinePeriodEpic,
  cancelOfflinePeriodEpic,
);
